import { FC, useEffect, useState, EffectCallback } from 'react'
import { useAppDispatch } from '../../../hooks/redux'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Breadcrumbs, Button, LottieLoader, Notification } from '@mondra/ui-components'
import { useAppSelector } from '../../../hooks/redux'
import { useFormik } from 'formik'
import { fetchTransport, getConfiguration } from './transportActions'
import { sortDropdown, transformFormValues } from '../../../shared/formFunctions'
import { formSchema, TAB_LABELS, TAB_ERRORS, TabsEnum } from './utils'
import {
  updateTransportActivity,
  publishTransportActivity,
  unPublishTransportActivity,
  createTransportActivity,
} from './api'

import { usePrompt } from '../../../hooks/usePrompt'

import { handlePublishStatus, setDefaultTransport } from '../../../redux/transportSlice'

import InputItem from '../../../components/InputItem'
import { showCorrelationIdError } from '../../../redux/correlationIdSlice'
import './index.scss'
import ActivityRightPanel from '../../../components/ActivityRightPanel'
import { ActivityTypes } from '../../../const'
import { Impacts } from './Impacts'
import { ContentTabs } from '../../../components/ContentTabs'

const Transport: FC = ({}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  let [searchParams] = useSearchParams()
  const { loading, transport, configuration } = useAppSelector(state => state.transportSlice)
  const { loading: companiesLoading } = useAppSelector(state => state.companiesSlice)
  const [showNotification, setShowNotification] = useState<boolean>(false)
  const [showError, setShowError] = useState<string>('')
  const [errors, setErrors] = useState<[any, string[]][]>()
  const [showPublished, setShowPublished] = useState<boolean>(false)
  let transportId = searchParams.get('itemId') || ''
  const isNew = transportId === 'new'
  const [selectedTab, setSelectedTab] = useState<number>(0)

  useEffect((): ReturnType<EffectCallback> => {
    if (!companiesLoading) {
      if (transportId === '') {
        navigate('/not-found')
      }
      dispatch(getConfiguration())
      if (!isNew) {
        dispatch(fetchTransport(transportId, () => navigate('/food/transport-activity')))
      }
      return () => {
        dispatch(setDefaultTransport())
      }
    }
  }, [companiesLoading, isNew])

  useEffect(() => {
    formik.setValues({ ...transport })
  }, [transport])

  const formik = useFormik({
    initialValues: {
      ...transport,
    },
    validationSchema: formSchema,
    onSubmit: async () => {
      if (transport.state === 0) {
        publishTransportActivity(transportId)
          .then(() => {
            dispatch(handlePublishStatus(1))
            setShowPublished(true)
            setTimeout(() => {
              setShowPublished(false)
            }, 4000)
          })
          .catch(error => setShowError(error.response.data.title))
      } else {
        unPublishTransportActivity(transportId)
          .then(() => {
            dispatch(handlePublishStatus(0))
            setShowPublished(true)
            setTimeout(() => {
              setShowPublished(false)
            }, 4000)
          })
          .catch(error => setShowError(error.response.data.title))
      }
    },
  })

  const getTabErrors = (tab: TabsEnum) => {
    return TAB_ERRORS[tab].some(value => formik.touched[value] && !!formik.errors[value])
  }

  const tabs = [
    {
      label: TAB_LABELS[TabsEnum.TRANSPORTACTIVITYITEM],
      hasErrors: getTabErrors(TabsEnum.TRANSPORTACTIVITYITEM),
    },
    {
      label: TAB_LABELS[TabsEnum.IMPACTS],
      hasErrors: getTabErrors(TabsEnum.IMPACTS),
    },
  ]

  const saveDraft = async e => {
    e.preventDefault()
    if (formik.isSubmitting) {
      return
    }
    formik.setSubmitting(true)
    const { values } = formik

    const tValues = transformFormValues(values)

    if (isNew) {
      try {
        await createTransportActivity(tValues).then(() => {
          navigate('/food/transport-activity')
        })
      } catch (error: any) {
        if (error.showCorrelationId) {
          dispatch(showCorrelationIdError(error.CorrelationId))
        } else {
          setShowError(
            typeof error.response.data === 'string'
              ? error.response.data
              : typeof error.response.data.errors === 'string'
              ? error.response.data.errors
              : Object.values(error.response.data.errors).toString().replace(/!,/g, '!\n')
          )
          setTimeout(() => {
            setShowError('')
          }, 5000)
          setErrors(
            typeof error.response.data.errors === 'string'
              ? error.response.data.errors
              : Object.entries(error.response.data.errors)
          )
        }
      } finally {
        formik.setSubmitting(false)
      }
    } else {
      try {
        await updateTransportActivity(transportId, tValues)
          .then(() =>
            dispatch(fetchTransport(transportId, () => navigate('/food/transport-activity')))
          )
          .then(() => {
            setShowNotification(true)
            setTimeout(() => {
              setShowNotification(false)
            }, 5000)
          })
      } catch (error: any) {
        if (error.showCorrelationId) {
          dispatch(showCorrelationIdError(error.CorrelationId))
        } else {
          setShowError(
            typeof error.response.data === 'string'
              ? error.response.data
              : typeof error.response.data.errors === 'string'
              ? error.response.data.errors
              : Object.values(error.response.data.errors).toString().replace(/!,/g, '!\n')
          )
          setTimeout(() => {
            setShowError('')
          }, 5000)
          setErrors(
            typeof error.response.data.errors === 'string'
              ? error.response.data.errors
              : Object.entries(error.response.data.errors)
          )
        }
      } finally {
        formik.setSubmitting(false)
      }
    }
  }

  usePrompt(
    'There are unsaved changes, do you want to leave?',
    !isNew ? JSON.stringify(transport) !== JSON.stringify(formik.values) : false
  )

  return loading !== true || isNew ? (
    <>
      <div className="piece content">
        {showError !== '' && (
          <div className={'piece-notification'}>
            <Notification type="error" label="Oops:" description={showError} />
          </div>
        )}
        {showPublished && (
          <div className={'piece-notification'}>
            <Notification
              type="success"
              label="Success:"
              description="Status was updated!"
              isToast={false}
            />
          </div>
        )}
        {showNotification && (
          <div className={'piece-notification'}>
            <Notification
              type="success"
              label="Success:"
              description="Item was updated!"
              isToast={false}
            />
          </div>
        )}
        <div className="mb-3 mt-3">
          <Breadcrumbs
            routes={[
              {
                path: '/food/transport-activity',
                title: 'Transport Activity Table',
              },
              {
                path: `/food/transport-activity/transport?itemId=${transportId}`,
                title: 'Transport Activity Item',
              },
            ]}
          />
        </div>
        {!isNew && (
          <>
            <div className="piece-header">
              <div
                className={`piece-header-status ${
                  transport.state === 0 ? 'bg-red-500' : 'bg-emerald-500'
                }`}
              >
                {transport.state === 0 ? 'Not Published' : 'Published'}
              </div>
              {transport?.lastPublishedDate ? (
                <span className={'inputItem-header'}>
                  {`Last published date: ${new Date(transport.lastPublishedDate).toLocaleString()}
                `}
                </span>
              ) : (
                ''
              )}
            </div>
          </>
        )}

        <ContentTabs
          items={tabs}
          selectedIndex={selectedTab}
          onChange={index => setSelectedTab(index)}
        />

        <p className={'piece-description mb-7 mt-1 decoration-gray-600'}>
          This database is a collation of resource type involved in transporting of different food
          products under different conditions. All transport activity data has been reported for a
          functional unit of <b>1 kg of the product transported</b>.
        </p>
        <form onSubmit={formik.handleSubmit} className="piece-content">
          {selectedTab === 0 && (
            <div>
              <h5 className={'mt-5'}>Inputs</h5>
              <div className="piece-content-inputs">
                <InputItem
                  name="Transport Resources"
                  description="Choose a transport resources"
                  type="select"
                  formik={formik}
                  showAsterisk
                  defaultValue={formik.values.resource}
                  onChange={formik.handleChange}
                  formikName="resource"
                  options={sortDropdown(
                    configuration.transportResources,
                    formik.values.resource,
                    'description'
                  )}
                />
                <InputItem
                  name="Temperature Conditions"
                  description="Choose a temperature conditions"
                  type="select"
                  defaultValue={formik.values.temperatureConditions}
                  formik={formik}
                  showAsterisk
                  onChange={formik.handleChange}
                  formikName="temperatureConditions"
                  options={sortDropdown(
                    configuration.temperatureConditions,
                    formik.values.temperatureConditions,
                    'description'
                  )}
                />
                <InputItem
                  name="Medium"
                  description="Select"
                  type="select"
                  defaultValue={formik.values.medium}
                  onChange={formik.handleChange}
                  formikName="medium"
                  formik={formik}
                  showAsterisk
                  options={sortDropdown(configuration.medium, formik.values.medium, 'description')}
                />
                <InputItem
                  name="Short description"
                  description="Enter short description please"
                  type="input"
                  onChange={formik.handleChange}
                  formik={formik}
                  defaultValue={formik.values.shortDescription || ''}
                  formikName="shortDescription"
                />
                <InputItem
                  name="Country name"
                  description="Select country"
                  type="select"
                  onChange={formik.handleChange}
                  formikName="countryId"
                  defaultValue={formik.values.countryId ? formik.values.countryId : -1}
                  formik={formik}
                  options={sortDropdown(
                    configuration.countries,
                    formik.values.countryId ?? -1,
                    'countryName'
                  )}
                />
                <InputItem
                  name="Country ISO Code"
                  description="Please select the country in which the data was collected."
                  type="select"
                  defaultValue={formik.values.countryId ? formik.values.countryId : -1}
                  onChange={formik.handleChange}
                  formik={formik}
                  formikName="countryId"
                  options={sortDropdown(
                    configuration.countries,
                    formik.values.countryId ?? -1,
                    'countryISO'
                  )}
                />
                <InputItem
                  name="Region Identifier"
                  description="Select location"
                  type="select"
                  defaultValue={formik.values.regionIdentifierId}
                  onChange={formik.handleChange}
                  formikName="regionIdentifierId"
                  showAsterisk
                  formik={formik}
                  options={sortDropdown(
                    configuration.regions,
                    formik.values.regionIdentifierId,
                    'name'
                  )}
                />
                <InputItem
                  name="Ref. Quantity"
                  description="Default value is 1!"
                  type="input"
                  addonTitle="kg.km"
                  formik={formik}
                  showAsterisk
                  defaultValue={formik.values.refQuantity}
                  formikName="refQuantity"
                />
                <InputItem
                  name="External code"
                  description=""
                  type="input"
                  disabled
                  formik={formik}
                  onChange={formik.handleChange}
                  defaultValue={formik.values.code || ''}
                  formikName="code"
                />
              </div>

              <h5 className={'mt-5'}>Data Quality</h5>
              <div className={'piece-content-inputs mb-5'}>
                <InputItem
                  name="Secondary Data Reference Source Type"
                  description="If multiple references sources where used to generate this record please indicate the overall reference source type by select the reference where the majority of the data was retrieved from, if this is not possible please select the source type with the lowest ranking. If this is not possible take the reference which was published dating back the longest. Please note “Peer-reviewed published literature” can be a journal, conference publication of book chapter."
                  type="select"
                  defaultValue={formik.values.secondaryDataReferenceSourceType}
                  onChange={formik.handleChange}
                  formik={formik}
                  showAsterisk
                  formikName="secondaryDataReferenceSourceType"
                  options={sortDropdown(
                    configuration.secondaryDataReferenceSourceType,
                    formik.values.secondaryDataReferenceSourceType,
                    'description'
                  )}
                />
                <InputItem
                  disabled
                  name="Data quality reliability score"
                  description="Read only"
                  type="input"
                  formik={formik}
                  defaultValue={formik.values.dataQualityReliabilityScore || ''}
                  formikName="dataQualityReliabilityScore"
                />
                <InputItem
                  name="Sampling End Date"
                  description="The date in which the site in its current form ceased to exist or in which data sampling ended in YYYY. If the data was equally collected from multiple studies please select the sourcing end year from the study published the furthest in the past."
                  type="input"
                  onChange={formik.handleChange}
                  formik={formik}
                  defaultValue={formik.values.referencesSamplingYear || ''}
                  formikName="referencesSamplingYear"
                />
                <InputItem
                  name="Reference Data Publication Year"
                  description="The year in which most of the data was published. If the data was equally collected from multiple studies please select the publication year from the study published the furthest in the past. "
                  type="input"
                  onChange={formik.handleChange}
                  formik={formik}
                  showAsterisk
                  defaultValue={
                    formik.values.referenceDataPublicationYear === 0
                      ? ''
                      : formik.values.referenceDataPublicationYear
                  }
                  formikName="referenceDataPublicationYear"
                />
                <InputItem
                  name="Reference Sourcing Country"
                  description="Please select the Country Name identifier in which the data was collected (not published). Please select the country where most of the data was collected from. Only use publication country if no other information is available."
                  type="select"
                  defaultValue={
                    formik.values.referenceSourcingCountry
                      ? formik.values.referenceSourcingCountry
                      : -1
                  }
                  onChange={formik.handleChange}
                  formik={formik}
                  formikName="referenceSourcingCountry"
                  options={sortDropdown(
                    configuration.countries,
                    formik.values.referenceSourcingCountry ?? -1,
                    'countryName'
                  )}
                />
                <InputItem
                  name="Reference Country ISO Code"
                  description="Please select the Country Name identifier in which the data was collected (not published). Please select the country which most of the data was collected from. Only use publication country if no other information is available."
                  type="select"
                  defaultValue={
                    formik.values.referenceSourcingCountry
                      ? formik.values.referenceSourcingCountry
                      : -1
                  }
                  onChange={formik.handleChange}
                  formik={formik}
                  formikName="referenceSourcingCountry"
                  options={sortDropdown(
                    configuration.countries,
                    formik.values.referenceSourcingCountry ?? -1,
                    'countryISO'
                  )}
                />
                <InputItem
                  name="Reference Region Identifier"
                  description="The region identifier in which the data was collected. Please select the region where most of the data was collected from."
                  type="select"
                  defaultValue={formik.values.referenceRegionIdentifier}
                  onChange={formik.handleChange}
                  formikName="referenceRegionIdentifier"
                  formik={formik}
                  showAsterisk
                  options={sortDropdown(
                    configuration.regions,
                    formik.values.referenceRegionIdentifier,
                    'name'
                  )}
                />
              </div>
              <InputItem
                name="Data Quality Comment"
                description="Please provide any comments on assumption that were made to
                determine the criteria for data quality for this record taking
                multiple references into account."
                type="text"
                onChange={formik.handleChange}
                formik={formik}
                className={'w-full'}
                defaultValue={formik.values.dataQualityComment || ''}
                formikName="dataQualityComment"
              />
            </div>
          )}
          {selectedTab === 1 && <Impacts formik={formik} />}
          {errors && (
            <ul className="piece-errors">
              {errors.map(([key, value]) =>
                value.map(err => (
                  <li>
                    <b>{key}:</b> {err}
                  </li>
                ))
              )}
            </ul>
          )}
          <div className="piece-actions">
            {sessionStorage.getItem('permission') === 'true' && !isNew && (
              <Button
                type="submit"
                variant="tertiary"
                size="md"
                className="w-32"
                disabled={JSON.stringify(transport) !== JSON.stringify(formik.values)}
              >
                {transport.state === 0 ? 'Publish' : 'Unpublish'}
              </Button>
            )}
            <Button
              variant="primary"
              size="md"
              className="w-32"
              onClick={saveDraft}
              disabled={formik.isSubmitting}
            >
              Save
            </Button>
            {JSON.stringify(transport) !== JSON.stringify(formik.values) && (
              <b className="absolute bottom-0 text-sm text-rose-500">
                Please save before publishing new changes
              </b>
            )}
          </div>
        </form>
      </div>
      <ActivityRightPanel
        activityId={transportId}
        activityType={ActivityTypes.TransportActivity}
        auditCreatedBy={transport.researcherName}
        auditLastUpdatedBy={transport.lastUpdatedName}
        companyId={formik.values.companyId}
        isVerified={formik.values.isVerified}
        verificationComments={formik.values.verificationComments}
        verifiedByName={formik.values.verifiedByName}
        verifiedOn={formik.values.verifiedOn}
        onChange={formik.handleChange}
      />
    </>
  ) : (
    <div className="overlay">
      <LottieLoader
        lottieType="butterflyLottie"
        style={{ height: '10.25rem', width: '10.25rem' }}
      />
    </div>
  )
}

export default Transport
